import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Screen } from '../components/AppShell';
import { RegPlate, StatusPill, Icon, Skeleton, Sheet, Field, ProgressBar, Badge } from '../components/ui';
import { MarkSelector } from '../components/MarkSelector';
import { SignaturePad, type SignaturePadHandle } from '../components/SignaturePad';
import { api, type Asset, type Defect, type Inspection, type Signature } from '../lib/api';
import { useAction, useAutosave, useLoad } from '../lib/hooks';
import { loadDraft, saveDraft, clearDraft, queuePatch, takeQueued, clearQueue } from '../lib/offline';
import { useAuth } from '../store/auth';
import { FORMS, DEFECT_CODES, type MarkCode, type InspectionSection } from '../../../shared/forms';
import { formatIsoWeek } from '../../../shared/schedule';
import './inspection.css';

interface Payload {
  inspection: Inspection;
  signatures: Signature[];
  defects: Defect[];
  asset: Asset;
}

type Marks = Record<string, { mc: MarkCode; note: string | null }>;

export default function InspectionRun() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, loading, error, reload } = useLoad<Payload>(() => api.get(`/inspections/${id}`), [id]);

  const [marks, setMarks] = useState<Marks>({});
  const [step, setStep] = useState(0);
  const [ready, setReady] = useState(false);
  const [unsynced, setUnsynced] = useState(false);

  const form = data ? FORMS[data.inspection.form_code] : null;
  const sections = form?.sections ?? [];
  // Sections, then the measurements page, then sign-off.
  const totalSteps = sections.length + 2;

  useEffect(() => {
    if (!data || ready) return;
    let cancelled = false;

    (async () => {
      const server = data.inspection.items as Marks;
      // The local copy is written on every tap, so where the two disagree the
      // device is the newer one - it may hold marks that never reached the
      // server because the yard had no signal.
      const draft = await loadDraft(data.inspection.id);
      if (cancelled) return;
      setMarks(draft ? { ...server, ...(draft.marks as Marks) } : server);
      setUnsynced(Boolean(await takeQueued(data.inspection.id)));
      setReady(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [data, ready]);

  const autosave = useAutosave<{ items?: Marks; [key: string]: unknown }>(
    async (payload) => {
      try {
        await api.patch(`/inspections/${id}`, payload);
        // Anything stranded by an earlier failure goes up with this success.
        const queued = await takeQueued(id!);
        if (queued) {
          await api.patch(`/inspections/${id}`, queued);
          await clearQueue(id!);
        }
        setUnsynced(false);
      } catch (err) {
        await queuePatch(id!, payload);
        setUnsynced(true);
        throw err;
      }
    },
    {
      // Marks are queued one tap at a time; without merging, tapping faster
      // than the debounce sent only the last one and the rest never left the
      // device. That is what produced "73 of 73 marked" here and
      // "26 items not yet marked" on the server.
      merge: (previous, next) => ({
        ...previous,
        ...next,
        ...(previous.items || next.items
          ? { items: { ...(previous.items ?? {}), ...(next.items ?? {}) } }
          : {}),
      }),
    },
  );

  const setMark = useCallback(
    (key: string, mc: MarkCode) => {
      setMarks((current) => {
        const next = { ...current, [key]: { mc, note: current[key]?.note ?? null } };
        autosave.queue({ items: { [key]: next[key] } as Marks });
        if (id) void saveDraft(id, next, {});
        return next;
      });
    },
    [autosave, id],
  );

  const setNote = useCallback(
    (key: string, note: string) => {
      setMarks((current) => {
        const existing = current[key];
        if (!existing) return current;
        const next = { ...current, [key]: { ...existing, note } };
        autosave.queue({ items: { [key]: next[key] } as Marks });
        if (id) void saveDraft(id, next, {});
        return next;
      });
    },
    [autosave, id],
  );

  const markSectionSatisfactory = useCallback(
    (section: InspectionSection) => {
      setMarks((current) => {
        const next = { ...current };
        const changed: Marks = {};
        for (const item of section.items) {
          // Never overwrite something already marked - a bulk action must not
          // quietly clear a defect the fitter has recorded.
          if (next[item.key]) continue;
          next[item.key] = { mc: 'S', note: null };
          changed[item.key] = next[item.key];
        }
        if (Object.keys(changed).length) {
          autosave.queue({ items: changed });
          if (id) void saveDraft(id, next, {});
        }
        return next;
      });
    },
    [autosave, id],
  );

  useEffect(() => {
    if (!id) return;
    const retry = async () => {
      const queued = await takeQueued(id);
      if (!queued) return;
      try {
        await api.patch(`/inspections/${id}`, queued);
        await clearQueue(id);
        setUnsynced(false);
      } catch {
        /* still offline - the queue keeps waiting */
      }
    };
    window.addEventListener('online', retry);
    return () => window.removeEventListener('online', retry);
  }, [id]);

  const progress = useMemo(() => {
    const all = sections.flatMap((s) => s.items);
    const done = all.filter((i) => marks[i.key]).length;
    const defects = all.filter((i) => marks[i.key] && DEFECT_CODES.includes(marks[i.key].mc)).length;
    const monitors = all.filter((i) => marks[i.key]?.mc === 'M').length;
    return { done, total: all.length, defects, monitors, remaining: all.length - done };
  }, [sections, marks]);

  if (loading && !data) {
    return (
      <Screen title="Inspection" back="/" hideTabs>
        <Skeleton variant="detail" rows={4} />
      </Screen>
    );
  }

  if (error || !data || !form) {
    return (
      <Screen title="Inspection" back="/" hideTabs>
        <div className="notice notice-danger">{error ?? 'Not found.'}</div>
      </Screen>
    );
  }

  const { inspection, asset } = data;

  if (inspection.locked_at) {
    navigate(`/inspections/${inspection.id}`, { replace: true });
    return null;
  }

  const section = sections[step];
  const onMeasurements = step === sections.length;
  const onSignOff = step === sections.length + 1;

  const stepTitle = onSignOff ? 'Sign off' : onMeasurements ? 'Measurements' : section.title;

  return (
    <Screen
      title={stepTitle}
      subtitle={
        <span className="row row-tight" style={{ gap: 6 }}>
          <RegPlate reg={asset.reg_no} fleetNo={asset.fleet_no} size="sm" />
          <span className="mono tiny">{inspection.ref}</span>
        </span>
      }
      back={() => {
        autosave.flush();
        navigate(`/fleet/${asset.id}`);
      }}
      hideTabs
      action={<SaveState state={autosave.state} />}
    >
      <div className="stack-4">
        {unsynced ? (
          <div className="notice notice-warn" role="status">
            <Icon name="warning" size={18} />
            <span className="small">
              Some marks have not reached the server yet. They are saved on this device and will sync
              on their own once you have signal - do not clear your browser data.
            </span>
          </div>
        ) : null}

        {/* ---- Progress ---- */}
        <div className="runbar">
          <ProgressBar
            value={progress.done}
            total={progress.total}
            tone={progress.defects > 0 ? 'hazard' : progress.done === progress.total ? 'ok' : 'brand'}
          />
          <div className="row spread tiny">
            <span className="muted">
              <strong className="num">{progress.done}</strong> of {progress.total} marked
            </span>
            <span className="row row-tight" style={{ gap: 8 }}>
              {progress.defects > 0 ? (
                <span style={{ color: 'var(--danger)' }}>{progress.defects} defect{progress.defects === 1 ? '' : 's'}</span>
              ) : null}
              {progress.monitors > 0 ? (
                <span style={{ color: 'var(--info)' }}>{progress.monitors} to monitor</span>
              ) : null}
            </span>
          </div>
        </div>

        {/* ---- Step navigation ---- */}
        <nav className="stepstrip" aria-label="Inspection sections">
          {sections.map((s, index) => {
            const done = s.items.filter((i) => marks[i.key]).length;
            const complete = done === s.items.length;
            const hasDefect = s.items.some((i) => marks[i.key] && DEFECT_CODES.includes(marks[i.key].mc));
            return (
              <button
                key={s.key}
                type="button"
                className={`stepchip${index === step ? ' is-on' : ''}${complete ? ' is-done' : ''}${hasDefect ? ' has-defect' : ''}`}
                onClick={() => setStep(index)}
                aria-current={index === step ? 'step' : undefined}
              >
                <span className="stepchip-name">{shortName(s.title)}</span>
                <span className="stepchip-count">
                  {done}/{s.items.length}
                </span>
              </button>
            );
          })}
          <button
            type="button"
            className={`stepchip${onMeasurements ? ' is-on' : ''}`}
            onClick={() => setStep(sections.length)}
          >
            <span className="stepchip-name">Measure</span>
            <span className="stepchip-count">Page 2</span>
          </button>
          <button
            type="button"
            className={`stepchip${onSignOff ? ' is-on' : ''}`}
            onClick={() => setStep(sections.length + 1)}
          >
            <span className="stepchip-name">Sign</span>
            <span className="stepchip-count">{progress.remaining === 0 ? 'Ready' : `${progress.remaining} left`}</span>
          </button>
        </nav>

        {/* ---- Body ---- */}
        {onSignOff ? (
          <SignOff
            data={data}
            progress={progress}
            onDone={reload}
            flush={autosave.flush}
            resync={() => api.patch(`/inspections/${id}`, { items: marks })}
          />
        ) : onMeasurements ? (
          <Measurements inspection={inspection} asset={asset} form={form} onSave={(p) => autosave.queue(p)} />
        ) : (
          <>
            <div className="row spread">
              <h2 className="section-title">{section.title}</h2>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => markSectionSatisfactory(section)}
                disabled={section.items.every((i) => marks[i.key])}
              >
                <Icon name="check" size={15} />
                All satisfactory
              </button>
            </div>

            <div className="itemlist">
              {section.items.map((item) => {
                const mark = marks[item.key];
                const needsNote = mark && mark.mc !== 'S' && mark.mc !== 'NA';
                return (
                  <div key={item.key} className={`item${mark ? ' is-marked' : ''}`}>
                    <div className="item-head">
                      <span className="item-label">{item.label}</span>
                      {item.im ? (
                        <span className="item-im" title={`DVSA Inspection Manual item ${item.im}`}>
                          IM {item.im}
                        </span>
                      ) : null}
                    </div>
                    <MarkSelector
                      value={mark?.mc}
                      onChange={(mc) => setMark(item.key, mc)}
                      itemLabel={item.label}
                    />
                    {needsNote ? (
                      <textarea
                        className="textarea item-note"
                        rows={2}
                        placeholder={
                          mark.mc === 'M'
                            ? 'What to watch, and the measurement if you took one'
                            : 'Describe the fault — this becomes the defect record'
                        }
                        value={mark.note ?? ''}
                        onChange={(e) => setNote(item.key, e.target.value)}
                      />
                    ) : null}
                  </div>
                );
              })}
            </div>

            <div className="actionbar">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                disabled={step === 0}
              >
                Back
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setStep((s) => Math.min(totalSteps - 1, s + 1))}
              >
                {step === sections.length - 1 ? 'Measurements' : 'Next section'}
                <Icon name="chevron" size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </Screen>
  );
}

function shortName(title: string) {
  return title
    .replace('Ground Level and Under Vehicle', 'Ground')
    .replace('Under/Alongside Trailer', 'Underside')
    .replace('Markings, Lamps and Reflectors', 'Lamps')
    .replace('Steering - Rear Steer & Drawbar Trailers', 'Steering')
    .replace('Engine Compartment', 'Engine')
    .replace('Inside Vehicle', 'Cab');
}

function SaveState({ state }: { state: 'idle' | 'saving' | 'saved' | 'error' }) {
  if (state === 'idle') return null;
  return (
    <span className={`savestate savestate-${state}`}>
      {state === 'saving' ? 'Saving…' : state === 'saved' ? 'Saved' : 'Not saved'}
    </span>
  );
}

/* ------------------------------------------------------------------ *
 * Page 2 measurements
 * ------------------------------------------------------------------ */

function Measurements({
  inspection,
  asset,
  form,
  onSave,
}: {
  inspection: Inspection;
  asset: Asset;
  form: (typeof FORMS)['VEH001'];
  onSave: (payload: Record<string, unknown>) => void;
}) {
  const [header, setHeader] = useState({
    inspectionDate: inspection.inspection_date ?? '',
    dateIn: inspection.date_in ?? '',
    location: inspection.location ?? '',
    odometer: inspection.odometer != null ? String(inspection.odometer) : '',
  });
  const [brake, setBrake] = useState<Record<string, any>>(inspection.brake_test ?? {});
  const [temp, setTemp] = useState<Record<string, any>>(inspection.brake_temp ?? {});
  const [tyres, setTyres] = useState<Record<string, any>>(inspection.tyres ?? {});
  const [comments, setComments] = useState(inspection.comments ?? '');
  const [tacho, setTacho] = useState(inspection.tacho_download ?? '');

  const push = (patch: Record<string, unknown>) => onSave(patch);

  const axleCount = Math.min(Math.max(asset.axles ?? 3, 1), 4);
  const axles = Array.from({ length: axleCount }, (_, i) => i + 1);

  const setBrakeField = (key: string, value: unknown) => {
    const next = { ...brake, [key]: value };
    setBrake(next);
    push({ brakeTest: next });
  };

  const setTempField = (key: string, value: unknown) => {
    const next = { ...temp, [key]: value };
    setTemp(next);
    push({ brakeTemp: next });
  };

  const setTyreField = (key: string, value: unknown) => {
    const next = { ...tyres, [key]: value };
    setTyres(next);
    push({ tyres: next });
  };

  return (
    <div className="stack-5">
      <section className="stack">
        <h2 className="section-title">Header</h2>
        <div className="card card-pad stack-4">
          <div className="grid-2 collapse">
            <Field label="Inspection date" hint={header.inspectionDate ? formatIsoWeek(header.inspectionDate) : undefined}>
              <input
                className="input"
                type="date"
                value={header.inspectionDate}
                onChange={(e) => {
                  setHeader((h) => ({ ...h, inspectionDate: e.target.value }));
                  push({ inspectionDate: e.target.value });
                }}
              />
            </Field>
            <Field label="Date in">
              <input
                className="input"
                type="date"
                value={header.dateIn}
                onChange={(e) => {
                  setHeader((h) => ({ ...h, dateIn: e.target.value }));
                  push({ dateIn: e.target.value });
                }}
              />
            </Field>
          </div>
          <Field label="Location of inspection">
            <input
              className="input"
              value={header.location}
              onChange={(e) => {
                setHeader((h) => ({ ...h, location: e.target.value }));
                push({ location: e.target.value });
              }}
            />
          </Field>
          <Field label={asset.kind === 'trailer' ? 'Hubometer' : 'Odometer reading'}>
            <input
              className="input"
              type="number"
              inputMode="numeric"
              value={header.odometer}
              onChange={(e) => {
                setHeader((h) => ({ ...h, odometer: e.target.value }));
                push({ odometer: e.target.value === '' ? null : Number(e.target.value) });
              }}
            />
          </Field>
        </div>
      </section>

      <section className="stack">
        <h2 className="section-title">Braking performance assessment</h2>
        <div className="card card-pad stack-4">
          <Field label="Method">
            <select
              className="select"
              value={brake.method ?? ''}
              onChange={(e) => setBrakeField('method', e.target.value)}
            >
              <option value="">— Select —</option>
              <option value="rbt">Roller brake test</option>
              <option value="decelerometer">Decelerometer</option>
              <option value="ebpms">EBPMS</option>
            </select>
          </Field>

          <div className="grid-2 collapse">
            <Field label="Laden state">
              <select
                className="select"
                value={brake.laden ?? ''}
                onChange={(e) => setBrakeField('laden', e.target.value)}
              >
                <option value="">— Select —</option>
                <option value="laden">Laden</option>
                <option value="unladen">Unladen</option>
              </select>
            </Field>
            <Field label="Measured weight (kg)">
              <input
                className="input"
                type="number"
                inputMode="numeric"
                value={brake.weight ?? ''}
                onChange={(e) => setBrakeField('weight', e.target.value)}
              />
            </Field>
          </div>

          <div className="stack-2">
            <span className="label">Efficiency (%)</span>
            {form.brakeRefs.map((ref) => (
              <div key={ref.im} className="measure-row">
                <span className="measure-label">
                  <Badge>IM {ref.im}</Badge> {ref.label}
                </span>
                <input
                  className="input measure-input"
                  type="number"
                  inputMode="decimal"
                  placeholder="%"
                  value={brake[`eff${ref.im}`] ?? ''}
                  onChange={(e) => setBrakeField(`eff${ref.im}`, e.target.value)}
                />
              </div>
            ))}
          </div>

          <Field label="RBT printout attached">
            <div className="segmented">
              {['yes', 'no'].map((option) => (
                <button
                  key={option}
                  type="button"
                  className={`segment${brake.printoutAttached === option ? ' is-on' : ''}`}
                  onClick={() => setBrakeField('printoutAttached', option)}
                >
                  {option === 'yes' ? 'Yes' : 'No'}
                </button>
              ))}
            </div>
          </Field>

          <Field label="Overall result">
            <div className="segmented">
              {['satisfactory', 'unsatisfactory'].map((option) => (
                <button
                  key={option}
                  type="button"
                  className={`segment${brake.result === option ? ' is-on' : ''}`}
                  onClick={() => setBrakeField('result', option)}
                >
                  {option === 'satisfactory' ? 'Satisfactory' : 'Unsatisfactory'}
                </button>
              ))}
            </div>
          </Field>
        </div>
      </section>

      <section className="stack">
        <h2 className="section-title">Brake temperature assessment</h2>
        <div className="card card-pad stack-4">
          <Field label="Ambient air temperature (°C)">
            <input
              className="input"
              type="number"
              inputMode="decimal"
              value={temp.ambient ?? ''}
              onChange={(e) => setTempField('ambient', e.target.value)}
            />
          </Field>
          <div
            className="axlegrid"
            style={{ gridTemplateColumns: `2.75rem repeat(${axleCount}, minmax(0, 1fr))` }}
          >
            <span className="axlegrid-corner" />
            {axles.map((axle) => (
              <span key={axle} className="axlegrid-head">
                Axle {axle}
              </span>
            ))}
            {(['N/S', 'O/S'] as const).map((side) => (
              <div key={side} className="axlegrid-row" style={{ display: 'contents' }}>
                <span className="axlegrid-side">{side}</span>
                {axles.map((axle) => (
                  <input
                    key={`${side}${axle}`}
                    className="input axlegrid-input"
                    type="number"
                    inputMode="decimal"
                    aria-label={`${side} axle ${axle} temperature`}
                    value={temp[`${side}-${axle}`] ?? ''}
                    onChange={(e) => setTempField(`${side}-${axle}`, e.target.value)}
                  />
                ))}
              </div>
            ))}
          </div>
          <p className="hint">Degrees Celsius, taken at each hub.</p>
        </div>
      </section>

      <section className="stack">
        <h2 className="section-title">Tread depth &amp; pressure</h2>
        <div className="card card-pad stack-4">
          {axles.map((axle) => {
            /*
             * Drive and rear axles usually carry twin wheels - four tyres on
             * the axle, an inner and an outer each side - so a single N/S and
             * O/S box cannot record them. Steer axles are single, and tag and
             * trailer axles vary, so it is per-axle rather than assumed.
             */
            const twin = Boolean(tyres[`twin-${axle}`]);
            const positions = twin
              ? ([
                  ['N/S', 'outer'],
                  ['N/S', 'inner'],
                  ['O/S', 'inner'],
                  ['O/S', 'outer'],
                ] as const)
              : ([
                  ['N/S', ''],
                  ['O/S', ''],
                ] as const);

            return (
              <div key={axle} className="stack-2">
                <div className="row spread">
                  <span className="label">Axle {axle}</span>
                  <button
                    type="button"
                    className={`twin-toggle${twin ? ' is-on' : ''}`}
                    aria-pressed={twin}
                    onClick={() => setTyreField(`twin-${axle}`, twin ? '' : '1')}
                  >
                    {twin ? '4 tyres' : '2 tyres'}
                  </button>
                </div>
                <div className={twin ? 'tyre-grid is-twin' : 'tyre-grid'}>
                  {positions.map(([side, pos]) => {
                    const key = pos ? `${side}-${pos}-${axle}` : `${side}-${axle}`;
                    const label = pos ? `${side} ${pos}` : side;
                    return (
                      <div key={key} className="tyre-cell">
                        <span className="tyre-side">{label}</span>
                        <input
                          className="input tyre-input"
                          type="number"
                          inputMode="decimal"
                          placeholder="mm"
                          aria-label={`${label} axle ${axle} tread depth in millimetres`}
                          value={tyres[`tread-${key}`] ?? ''}
                          onChange={(e) => setTyreField(`tread-${key}`, e.target.value)}
                        />
                        <input
                          className="input tyre-input"
                          type="number"
                          inputMode="numeric"
                          placeholder="psi"
                          aria-label={`${label} axle ${axle} pressure in psi`}
                          value={tyres[`psi-${key}`] ?? ''}
                          onChange={(e) => setTyreField(`psi-${key}`, e.target.value)}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
          <p className="hint">
            Tread depth in millimetres, pressure in psi. Tap <strong>2 tyres</strong> to switch an
            axle to twin wheels. Legal minimum is 1mm across the central three-quarters.
          </p>
        </div>
      </section>

      <section className="stack">
        <h2 className="section-title">Inspector comments</h2>
        <textarea
          className="textarea"
          rows={4}
          value={comments}
          onChange={(e) => {
            setComments(e.target.value);
            push({ comments: e.target.value });
          }}
          placeholder="Anything the operator needs to know."
        />
      </section>

      {form.hasTachoDownload ? (
        <section className="stack">
          <h2 className="section-title">Digital tachograph</h2>
          <Field label="Data downloaded">
            <div className="segmented">
              {['yes', 'no'].map((option) => (
                <button
                  key={option}
                  type="button"
                  className={`segment${tacho === option ? ' is-on' : ''}`}
                  onClick={() => {
                    setTacho(option);
                    push({ tachoDownload: option });
                  }}
                >
                  {option === 'yes' ? 'Yes' : 'No'}
                </button>
              ))}
            </div>
          </Field>
        </section>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Sign off
 * ------------------------------------------------------------------ */

function SignOff({
  data,
  progress,
  onDone,
  flush,
  resync,
}: {
  data: Payload;
  progress: { done: number; total: number; defects: number; remaining: number };
  onDone: () => void;
  flush: () => Promise<void>;
  /** Pushes every mark held on this device, for when the server disagrees. */
  resync: () => Promise<unknown>;
}) {
  const user = useAuth((s) => s.user);
  const navigate = useNavigate();
  const { inspection, defects, signatures } = data;
  const { busy, error, run, setError } = useAction();

  const [sheet, setSheet] = useState<null | 'inspection' | 'rectification'>(null);
  const [sealed, setSealed] = useState(false);
  const [name, setName] = useState(user?.name ?? '');
  const [position, setPosition] = useState(user?.position ?? '');
  const [hasInk, setHasInk] = useState(false);
  const [allowIncomplete, setAllowIncomplete] = useState(false);
  const padRef = useRef<SignaturePadHandle>(null);

  const openDefects = defects.filter((d) => d.status === 'open');
  const signed = Boolean(inspection.signed_at);
  const form = FORMS[inspection.form_code];

  const submit = () =>
    run(async () => {
      const signature = padRef.current?.toDataURL();
      if (!signature) {
        setError('Please sign in the box.');
        return;
      }
      await flush();
      const endpoint = sheet === 'rectification' ? 'rectify' : 'sign';
      const body = { name, position, signature, allowIncomplete, allowOpenDefects: false };

      try {
        await api.post(`/inspections/${inspection.id}/${endpoint}`, body);
      } catch (err: any) {
        // The server thinks items are unmarked that this device has marked.
        // Push the whole set and try once more rather than making the fitter
        // hunt for items the sheet already shows as done.
        if (err?.status === 400 && Array.isArray(err?.data?.missing)) {
          await resync();
          await api.post(`/inspections/${inspection.id}/${endpoint}`, body);
        } else {
          throw err;
        }
      }
      setSheet(null);
      padRef.current?.clear();
      const refreshed = await api.get<Payload>(`/inspections/${inspection.id}`);
      if (refreshed.inspection.locked_at) {
        // The record is sealed server-side; the device copy is no longer needed.
        await clearDraft(inspection.id);
        // A moment to mark the one action that cannot be undone, then the record.
        setSealed(true);
        window.setTimeout(() => navigate(`/inspections/${inspection.id}`, { replace: true }), 1700);
      } else onDone();
    });

  const rectifyDefect = (defect: Defect, patch: Record<string, unknown>) =>
    run(async () => {
      await api.patch(`/inspections/defects/${defect.id}`, patch);
      onDone();
    });

  return (
    <div className="stack-5">
      {sealed ? (
        <div className="sealed" role="status" aria-live="polite">
          <div className="sealed-mark">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12.5 10 17.5 19 7" />
            </svg>
          </div>
          <h2>Record sealed</h2>
          <p className="muted">
            <span className="mono">{inspection.ref}</span> is locked and on file.
          </p>
        </div>
      ) : null}

      {error ? <div className="notice notice-danger">{error}</div> : null}

      {/* ---- Summary ---- */}
      <div className="card card-pad stack-4">
        <div className="row spread">
          <h2 className="section-title">Part 1 — Inspection</h2>
          {signed ? (
            <StatusPill state="ok" size="sm">
              Signed
            </StatusPill>
          ) : (
            <StatusPill state={progress.remaining === 0 ? 'due' : 'unknown'} size="sm">
              {progress.remaining === 0 ? 'Ready' : `${progress.remaining} unmarked`}
            </StatusPill>
          )}
        </div>

        <dl className="kv">
          <dt>Form</dt>
          <dd>
            {inspection.form_code} · {form.revision}
          </dd>
          <dt>Items marked</dt>
          <dd>
            {progress.done} of {progress.total}
          </dd>
          <dt>Defects</dt>
          <dd>{progress.defects === 0 ? 'None' : `${progress.defects} recorded`}</dd>
          {inspection.inspection_date ? (
            <>
              <dt>ISO week</dt>
              <dd>{formatIsoWeek(inspection.inspection_date)}</dd>
            </>
          ) : null}
        </dl>

        {!signed && progress.remaining > 0 ? (
          <div className="notice notice-warn">
            <div>
              <strong>{progress.remaining} items are not yet marked.</strong>
              <label className="row row-tight" style={{ marginTop: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={allowIncomplete}
                  onChange={(e) => setAllowIncomplete(e.target.checked)}
                />
                <span className="small">Sign anyway — I have deliberately left these blank</span>
              </label>
            </div>
          </div>
        ) : null}

        {signed ? (
          <div className="sig-block">
            {signatures
              .filter((s) => s.kind === 'inspection')
              .map((s) => (
                <SignatureCard key={s.id} signature={s} caption="Inspector" />
              ))}
          </div>
        ) : (
          <button
            className="btn btn-primary btn-block"
            onClick={() => setSheet('inspection')}
            disabled={progress.remaining > 0 && !allowIncomplete}
          >
            Sign as inspector
          </button>
        )}
      </div>

      {/* ---- Part 2 ---- */}
      {signed ? (
        <div className="card card-pad stack-4">
          <div className="row spread">
            <h2 className="section-title">Part 2 — Rectification</h2>
            <StatusPill state={openDefects.length === 0 ? 'ok' : 'overdue'} size="sm">
              {openDefects.length === 0 ? 'All clear' : `${openDefects.length} open`}
            </StatusPill>
          </div>

          {defects.length === 0 ? (
            <p className="small muted">No defects were recorded on this inspection.</p>
          ) : (
            <div className="defectlist">
              {defects.map((defect) => (
                <DefectRow
                  key={defect.id}
                  defect={defect}
                  busy={busy}
                  onSave={(patch) => rectifyDefect(defect, patch)}
                />
              ))}
            </div>
          )}

          <div className="notice notice-info">
            <span className="small">
              Signing below declares the vehicle is in a safe and roadworthy condition. The record locks
              permanently once signed.
            </span>
          </div>

          <button
            className="btn btn-primary btn-block"
            onClick={() => setSheet('rectification')}
            disabled={openDefects.length > 0}
          >
            {openDefects.length > 0
              ? `Rectify ${openDefects.length} defect${openDefects.length === 1 ? '' : 's'} first`
              : 'Sign roadworthiness declaration'}
          </button>
        </div>
      ) : null}

      {/* ---- Signature sheet ---- */}
      <Sheet
        open={sheet !== null}
        onClose={() => setSheet(null)}
        title={sheet === 'rectification' ? 'Roadworthiness declaration' : 'Inspector declaration'}
        footer={
          <>
            <button className="btn btn-ghost" onClick={() => setSheet(null)} disabled={busy}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={submit} disabled={busy || !hasInk || !name.trim()}>
              {busy ? 'Signing…' : 'Sign and lock'}
            </button>
          </>
        }
      >
        <div className="stack-4">
          <p className="small muted">
            {sheet === 'rectification'
              ? 'I consider the above defects have been rectified satisfactorily and the vehicle is now in a safe and roadworthy condition.'
              : 'The inspection of this vehicle has been conducted to meet the requirements set out by the Driver and Vehicle Standards Agency (DVSA) and the relevant vehicle manufacturer.'}
          </p>

          <Field label="Name">
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label="Position">
            <input className="input" value={position} onChange={(e) => setPosition(e.target.value)} />
          </Field>

          <SignaturePad ref={padRef} onChange={setHasInk} />
        </div>
      </Sheet>
    </div>
  );
}

function DefectRow({
  defect,
  busy,
  onSave,
}: {
  defect: Defect;
  busy: boolean;
  onSave: (patch: Record<string, unknown>) => void;
}) {
  const [action, setAction] = useState(defect.action_taken ?? '');
  const [doneBy, setDoneBy] = useState(defect.done_by ?? '');
  const rectified = defect.status === 'rectified';

  return (
    <div className={`defect${rectified ? ' is-done' : ''}`}>
      <div className="row spread row-top">
        <div className="grow">
          <div className="defect-desc">{defect.description}</div>
          <div className="row row-tight" style={{ marginTop: 4, flexWrap: 'wrap' }}>
            {defect.im_no ? <Badge>IM {defect.im_no}</Badge> : null}
            <Badge tone={defect.severity}>
              {defect.severity === 'safety' ? 'Safety' : defect.severity === 'repair' ? 'Repair' : 'Monitor'}
            </Badge>
          </div>
        </div>
        <StatusPill state={rectified ? 'ok' : 'overdue'} size="sm">
          {rectified ? 'Rectified' : 'Open'}
        </StatusPill>
      </div>

      {rectified ? (
        <p className="small muted" style={{ marginTop: 6 }}>
          {defect.action_taken}
          {defect.done_by ? ` — ${defect.done_by}` : ''}
        </p>
      ) : (
        <div className="stack-2" style={{ marginTop: 10 }}>
          <textarea
            className="textarea"
            rows={2}
            placeholder="Action taken to rectify"
            value={action}
            onChange={(e) => setAction(e.target.value)}
          />
          <div className="row">
            <input
              className="input grow"
              placeholder="Done by"
              value={doneBy}
              onChange={(e) => setDoneBy(e.target.value)}
            />
            <button
              className="btn btn-primary btn-sm"
              disabled={busy || !action.trim()}
              onClick={() => onSave({ status: 'rectified', actionTaken: action, doneBy })}
            >
              Rectified
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function SignatureCard({ signature, caption }: { signature: Signature; caption: string }) {
  return (
    <figure className="sigcard">
      <img src={signature.image} alt={`Signature of ${signature.name}`} />
      <figcaption>
        <strong>{signature.name}</strong>
        {signature.position ? <span className="dim"> · {signature.position}</span> : null}
        <div className="tiny dim">
          {caption} · {new Date(signature.signed_at).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}
        </div>
      </figcaption>
    </figure>
  );
}
